import { gql } from '@apollo/client';

// Fragments
export const INVOICE_FRAGMENT = gql`
  fragment InvoiceFields on Invoice {
    id
    invoiceNumber
    resolutionId
    resolutionType
    amount
    tax
    total
    status
    createdAt
    deletedAt
    deletedBy
    isSusceptibleToDeletion
    transmittedToDIAN
    customerName
    customerDocument
  }
`;

export const DELETION_CONFIG_FRAGMENT = gql`
  fragment DeletionConfigFields on DeletionConfig {
    id
    isActive
    timeRange {
      startHour
      startMinute
      endHour
      endMinute
    }
    deleteAmount
    deletePercentage
    frequency
    lastExecution
    totalDeleted
    amountDeleted
  }
`;

// Queries
export const GET_DASHBOARD_STATS = gql`
  query GetDashboardStats($startDate: DateTime, $endDate: DateTime) {
    getDashboardStats(startDate: $startDate, endDate: $endDate) {
      totalInvoices
      totalAmount
      susceptibleInvoices
      susceptibleAmount
      deletedInvoices
      deletedAmount
      deletionPercentage
    }
  }
`;

export const GET_INVOICES = gql`
  ${INVOICE_FRAGMENT}
  query GetInvoices(
    $resolutionType: ResolutionType
    $status: InvoiceStatus
    $startDate: DateTime
    $endDate: DateTime
    $limit: Int
    $offset: Int
  ) {
    getInvoices(
      resolutionType: $resolutionType
      status: $status
      startDate: $startDate
      endDate: $endDate
      limit: $limit
      offset: $offset
    ) {
      ...InvoiceFields
    }
  }
`;

export const GET_SUSCEPTIBLE_INVOICES = gql`
  ${INVOICE_FRAGMENT}
  query GetSusceptibleInvoices($startDate: DateTime, $endDate: DateTime) {
    getSusceptibleInvoices(startDate: $startDate, endDate: $endDate) {
      ...InvoiceFields
    }
  }
`;

export const GET_DELETED_INVOICES = gql`
  ${INVOICE_FRAGMENT}
  query GetDeletedInvoices($startDate: DateTime, $endDate: DateTime) {
    getDeletedInvoices(startDate: $startDate, endDate: $endDate) {
      ...InvoiceFields
    }
  }
`;

export const GET_ELECTRONIC_INVOICES = gql`
  ${INVOICE_FRAGMENT}
  query GetElectronicInvoices($startDate: DateTime, $endDate: DateTime) {
    getElectronicInvoices(startDate: $startDate, endDate: $endDate) {
      ...InvoiceFields
    }
  }
`;

export const GET_INVOICE_TYPE_STATS = gql`
  query GetInvoiceTypeStats($startDate: DateTime, $endDate: DateTime) {
    getInvoiceTypeStats(startDate: $startDate, endDate: $endDate) {
      electronic {
        count
        amount
      }
      susceptible {
        count
        amount
      }
    }
  }
`;

export const GET_DELETION_CONFIG = gql`
  ${DELETION_CONFIG_FRAGMENT}
  query GetDeletionConfig {
    getDeletionConfig {
      ...DeletionConfigFields
    }
  }
`;

export const GET_CASH_MODE_CONFIG = gql`
  query GetCashModeConfig {
    getCashModeConfig {
      isEnabled
      enabledBy
      enabledAt
    }
  }
`;

export const GET_RESOLUTIONS = gql`
  query GetResolutions {
    getResolutions {
      id
      type
      prefix
      startNumber
      endNumber
      currentNumber
      isActive
      transmitToDIAN
    }
  }
`;

export const GET_DELETION_LOGS = gql`
  query GetDeletionLogs(
    $startDate: DateTime
    $endDate: DateTime
    $limit: Int
    $offset: Int
  ) {
    getDeletionLogs(
      startDate: $startDate
      endDate: $endDate
      limit: $limit
      offset: $offset
    ) {
      id
      invoiceId
      invoiceNumber
      amount
      deletedAt
      deletedBy
      deletionType
      reason
    }
  }
`;

// Mutations
export const TOGGLE_CASH_MODE = gql`
  mutation ToggleCashMode($enabled: Boolean!) {
    toggleCashMode(enabled: $enabled) {
      isEnabled
      enabledBy
      enabledAt
    }
  }
`;

export const UPDATE_DELETION_CONFIG = gql`
  ${DELETION_CONFIG_FRAGMENT}
  mutation UpdateDeletionConfig($config: DeletionConfigInput!) {
    updateDeletionConfig(config: $config) {
      ...DeletionConfigFields
    }
  }
`;

export const DELETE_INVOICES_MANUALLY = gql`
  mutation DeleteInvoicesManually($input: DeleteInvoicesInput!) {
    deleteInvoicesManually(input: $input)
  }
`;

export const EXECUTE_SCHEDULED_DELETION = gql`
  mutation ExecuteScheduledDeletion {
    executeScheduledDeletion
  }
`;

export const ACTIVATE_RESOLUTION = gql`
  mutation ActivateResolution($resolutionId: ID!) {
    activateResolution(resolutionId: $resolutionId) {
      id
      type
      prefix
      startNumber
      endNumber
      currentNumber
      isActive
      transmitToDIAN
    }
  }
`;

// Subscriptions
export const INVOICE_CREATED_SUBSCRIPTION = gql`
  ${INVOICE_FRAGMENT}
  subscription InvoiceCreated {
    invoiceCreated {
      ...InvoiceFields
    }
  }
`;

export const INVOICE_DELETED_SUBSCRIPTION = gql`
  subscription InvoiceDeleted {
    invoiceDeleted {
      id
      invoiceId
      invoiceNumber
      amount
      deletedAt
      deletedBy
      deletionType
      reason
    }
  }
`;

export const CONFIG_UPDATED_SUBSCRIPTION = gql`
  ${DELETION_CONFIG_FRAGMENT}
  subscription ConfigUpdated {
    configUpdated {
      ...DeletionConfigFields
    }
  }
`;
